import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { OutlinedPrimaryButton } from "src/components/Default/LekUI/Button";
import { ImportDropzone } from "src/components/Modals/DecksImportModal/ImportDropzone";
import { ImportedFileCard } from "src/components/Modals/DecksImportModal/ImportedFileCard";
import { SectionHeading } from "src/components/Modals/DecksImportModal/SectionHeading";
import { Loading as _Loading } from "src/components/TextEditor";
import { isTablet } from "src/helpers/isMobile";
import { getAppConfig } from "src/hooks/useAppConfig";

const Loading = _Loading as unknown as ComponentType<{
	isLoading: boolean;
	children: ReactNode;
}>;

export function humanFileSize(size: number): string {
	const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
	return `${+(size / 1024 ** i).toFixed(2) * 1} ${
		["B", "kB", "MB", "GB", "TB"][i]
	}`;
}

export type UploadMode = "upload" | "photo";

interface UploadedFileData {
	file: Blob;
	name: string;
}

interface FileUploaderProps {
	mode: UploadMode;
	allowedFormats?: string;
	accept?: string;
	onFileChange: (file: UploadedFileData | null) => void;
	file: UploadedFileData | null;
	// Wybór wielu plików naraz (tylko mode="upload"); wszystkie trafiają do onFilesChange.
	multiple?: boolean;
	onFilesChange?: (files: UploadedFileData[]) => void;
}

export const FileUploader = ({
	mode,
	allowedFormats,
	accept,
	onFileChange,
	file,
	multiple = false,
	onFilesChange,
}: FileUploaderProps) => {
	const [loading, setLoading] = useState(false);
	const appConfig = getAppConfig();
	const { t } = useTranslation();
	const maxMB = appConfig.maxImageSizeMb;

	const previewUrl = useMemo(
		() => (file ? URL.createObjectURL(file.file) : undefined),
		[file],
	);

	useEffect(
		() => () => {
			if (previewUrl) URL.revokeObjectURL(previewUrl);
		},
		[previewUrl],
	);

	const handleFiles = (selectedFiles: File[]) => {
		if (selectedFiles.length === 0) return;

		if (selectedFiles.some((f) => f.size > maxMB * 1024 * 1024)) {
			toast.error(t("uploader.fileTooLarge", "Plik jest za duży"), {
				position: toast.POSITION.BOTTOM_RIGHT,
				pauseOnHover: false,
				autoClose: 3000,
			});
			return;
		}

		const uploaded = selectedFiles.map((f) => ({ file: f, name: f.name }));
		if (multiple && onFilesChange) {
			onFilesChange(uploaded);
		} else {
			onFileChange(uploaded[0]);
		}
	};

	const runCameraUpload = async (source: CameraSource) => {
		setLoading(true);
		let imagePath: string | undefined;
		try {
			const image = await Camera.getPhoto({
				quality: 100,
				allowEditing: true,
				resultType: CameraResultType.Uri,
				correctOrientation: true,
				webUseInput: true,
				source,
			});
			imagePath = image.webPath;
		} catch {
			setLoading(false);
			return;
		}

		try {
			if (!imagePath) throw new Error("Missing image path");
			const response = await fetch(imagePath);
			const blob = await response.blob();
			const fileName = imagePath.split("/").pop() || String(Date.now());
			onFileChange({ file: blob, name: fileName });
			setLoading(false);
		} catch {
			toast.error(t("errors.uploadImageProblem"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 3000,
			});
			setLoading(false);
		}
	};

	const formats = (allowedFormats ?? "")
		.split(",")
		.map((format) => format.trim())
		.filter(Boolean)
		.map((format) => `.${format}`);

	const extension = file?.name.includes(".")
		? file.name.split(".").pop()?.toUpperCase()
		: undefined;

	return (
		<div className="flex flex-col gap-20 w-100">
			<SectionHeading
				title={t("uploader.chooseFile", "Wybierz plik")}
				description={
					file
						? t(
								"uploader.fileReady",
								"Plik gotowy. Miniatura pokazuje, jak obraz będzie wyglądał.",
							)
						: t("uploader.maxSizeHint", {
								maxMB,
								defaultValue: "Maksymalny rozmiar: {{maxMB}} MB.",
							})
				}
			/>
			<Loading isLoading={loading}>
				{!file && (
					<div className="flex flex-col items-center gap-12 w-100">
						<ImportDropzone
							accept={accept ?? ""}
							multiple={multiple}
							formats={formats}
							onFiles={handleFiles}
							onPick={
								mode === "photo"
									? () => runCameraUpload(CameraSource.Photos)
									: undefined
							}
						/>
						{mode === "photo" && isTablet() && (
							<OutlinedPrimaryButton
								onClick={() => runCameraUpload(CameraSource.Camera)}
							>
								{t("uploader.takePhoto", "Zrób zdjęcie")}
							</OutlinedPrimaryButton>
						)}
					</div>
				)}
				{file && (
					<ImportedFileCard
						fileName={file.name}
						meta={[humanFileSize(file.file.size), extension]
							.filter(Boolean)
							.join(" · ")}
						imageSrc={previewUrl}
						onChange={() => onFileChange(null)}
					/>
				)}
			</Loading>
		</div>
	);
};
